<!-- eslint-disable max-len -->
<template>
  <ValidationObserver ref="form">
    <FormToolbar
      :title="edit ? 'Edit Voyage Report' : 'New Voyage Report'"
      :onSubmitClick="submitVoyageReport"
      :isSubmitting="isSubmitting"
      :onCloseClick="onCloseClick"
      :showDeleteButton="edit && isClientConnectedToServer"
      :onDeleteClick="openDeleteDialog"
    />
    <v-form class="mt-1">
      <v-container>
        <v-row>
          <v-col cols="12" md="4">
            <ValidationProvider
              name="Voyage No."
              rules="required"
              v-slot="{ errors }"
            >
              <v-text-field
                v-model="report.voyageNo"
                :error-messages="errors"
                :disabled="disableVoyageNumberEdit"
                label="Voyage Number"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4">
            <ValidationProvider
              name="Vessel"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="report.vesselId"
                :items="vessels"
                item-text="name"
                item-value="id"
                :error-messages="errors"
                label="Vessel"
                :disabled="disableVoyageNumberEdit"
              ></v-autocomplete>
            </ValidationProvider>
          </v-col>
          <v-col cols="12" md="4">
            <ValidationProvider
              name="Initial Port"
              rules="required"
              v-slot="{ errors }"
            >
              <v-autocomplete
                v-model="report.initialPortLocation"
                :items="ports"
                item-text="name"
                item-value="id"
                return-object
                :error-messages="errors"
                label="Initial Port"
              ></v-autocomplete>
            </ValidationProvider>
          </v-col>
        </v-row>
        <!-- Start, End, ATA, ATD -->
        <div v-if="edit">
          <v-row v-if="report.status != 'Pending'">
            <v-col cols="8" md="4" class="py-0 ma-0">
              <v-dialog
                ref="startDateMenu"
                v-model="startDateMenu"
                transition="scale-transition"
                width="unset"
              >
                <template v-slot:activator="{ on }">
                  <ValidationProvider
                    name="Start Date"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="report.startDate"
                      label="Start Date"
                      :error-messages="errors"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </ValidationProvider>
                </template>
                <v-date-picker
                  v-model="report.startDate"
                  @change="startDateSelected"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="startDateMenu = false"
                    >Cancel</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="4" md="2" class="py-0 ma-0">
              <v-dialog
                ref="startTimeMenu"
                v-model="startTimeMenu"
                transition="scale-transition"
                max-width="300px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    ref="time"
                    v-model="report.startTime"
                    label="Start Time"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="startTimeMenu"
                  v-model="report.startTime"
                  format="24hr"
                  full-width
                  @click:minute="startTimeMenu = false"
                ></v-time-picker>
              </v-dialog>
            </v-col>
          </v-row>

          <v-row v-if="report.status == 'Completed'">
            <v-col cols="8" md="4" class="py-0 ma-0">
              <v-dialog
                ref="endDateMenu"
                v-model="endDateMenu"
                transition="scale-transition"
                width="unset"
              >
                <template v-slot:activator="{ on }">
                  <ValidationProvider
                    name="Start Date"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      v-model="report.endDate"
                      label="End Date"
                      :error-messages="errors"
                      readonly
                      v-on="on"
                    ></v-text-field>
                  </ValidationProvider>
                </template>
                <v-date-picker
                  v-model="report.endDate"
                  @change="endDateSelected"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="endDateMenu = false"
                    >Cancel</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="4" md="2" class="py-0 ma-0">
              <v-dialog
                ref="endTimeMenu"
                v-model="endTimeMenu"
                transition="scale-transition"
                max-width="300px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    ref="time"
                    v-model="report.endTime"
                    label="End Time"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="endTimeMenu"
                  v-model="report.endTime"
                  format="24hr"
                  full-width
                  @click:minute="endTimeMenu = false"
                ></v-time-picker>
              </v-dialog>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="8" md="4" class="py-0 ma-0">
              <v-dialog
                ref="atdDateMenu"
                v-model="atdDateMenu"
                transition="scale-transition"
                width="unset"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="report.atdDate"
                    label="ATD Date"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="report.atdDate"
                  @change="atdDateSelected"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="atdDateMenu = false"
                    >Cancel</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="4" md="2" class="py-0 ma-0">
              <v-dialog
                ref="atdTimeMenu"
                v-model="atdTimeMenu"
                transition="scale-transition"
                max-width="300px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    ref="time"
                    v-model="report.atdTime"
                    label="ATD Time"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="atdTimeMenu"
                  v-model="report.atdTime"
                  format="24hr"
                  full-width
                  @click:minute="atdTimeMenu = false"
                ></v-time-picker>
              </v-dialog>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="8" md="4" class="py-0 ma-0">
              <v-dialog
                ref="ataDateMenu"
                v-model="ataDateMenu"
                transition="scale-transition"
                width="unset"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="report.ataDate"
                    label="ATA Date"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="report.ataDate"
                  @change="ataDateSelected"
                  no-title
                  scrollable
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="ataDateMenu = false"
                    >Cancel</v-btn
                  >
                </v-date-picker>
              </v-dialog>
            </v-col>
            <v-col cols="4" md="2" class="py-0 ma-0">
              <v-dialog
                ref="ataTimeMenu"
                v-model="ataTimeMenu"
                transition="scale-transition"
                max-width="300px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    ref="time"
                    v-model="report.ataTime"
                    label="ATA Time"
                    readonly
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-time-picker
                  v-if="ataTimeMenu"
                  v-model="report.ataTime"
                  format="24hr"
                  full-width
                  @click:minute="ataTimeMenu = false"
                ></v-time-picker>
              </v-dialog>
            </v-col>
          </v-row>
        </div>
        <v-row class="justify-start">
          <v-col cols="12" md="4" class="py-0 ma-0">
            <v-checkbox
              v-model="report.isNewVesselHirePeriod"
              label="New Hire Period"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <ConfirmDialog
      v-model="showDeleteDialog"
      title="Delete Voyage Report"
      description="Are you sure you want to delete this voyage?"
      :onConfirmClick="onDeleteClick"
      :onCloseClick="closeDeleteDialog"
    />
  </ValidationObserver>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { mapState, mapGetters } from 'vuex';
import FormToolbar from '../ui/FormToolbar.vue';
import ConfirmDialog from '../ui/ConfirmDialog.vue';
import '../../validators';
import dateTimeHelpers from '../../mixins/DateTimeHelpers';

export default {
  name: 'VoyageReportForm',
  components: {
    ValidationProvider,
    ValidationObserver,
    FormToolbar,
    ConfirmDialog,
  },
  props: {
    edit: Boolean,
    report: Object,
    onSubmitClick: Function,
    onCloseClick: Function,
    onDeleteClick: Function,
    isSubmitting: Boolean,
    bus: Object,
  },
  data() {
    return {
      startDateMenu: false,
      startTimeMenu: false,
      endDateMenu: false,
      endTimeMenu: false,
      ataDateMenu: false,
      ataTimeMenu: false,
      atdDateMenu: false,
      atdTimeMenu: false,
      showDeleteDialog: false,
      voyageReportStates: [],
    };
  },
  mounted() {
    this.bus.$on('resetForm', this.resetForm);
  },
  computed: {
    ...mapState(['voyageReportsStore', 'vesselsStore', 'locationsStore', 'userStore']),
    ...mapGetters({
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      isAdmin: 'userStore/isAdmin',
      getActiveSite: 'userStore/getActiveSite',
      getPorts: 'locationsStore/getPorts',
      getVesselsForSite: 'vesselsStore/getVesselsForSite',
    }),
    vessels() {
      return this.getVesselsForSite(this.activeSiteId);
    },
    ports() {
      return this.getPorts;
    },
    activeSiteId() {
      const site = this.getActiveSite;
      if (!site) {
        return null;
      }
      return site.id;
    },
    disableVoyageNumberEdit() {
      return this.edit && !this.isAdmin;
    },
  },
  methods: {
    resetForm() {
      this.$refs.form.reset();
    },
    submitVoyageReport() {
      this.$refs.form.validate()
        .then((isValid) => {
          if (isValid) {
            this.setReportDateTimeValue();
            this.setReportVesselNameFromVesselId();
            this.onSubmitClick();
          }
        });
    },
    setReportVesselNameFromVesselId() {
      // TODO - improvement - (return obj from selector and extract name)
      const vessel = this.vessels.find((x) => x.id === this.report.vesselId);
      this.report.vesselName = vessel.name || null;
    },
    setReportDateTimeValue() {
      const {
        startDate, endDate, ataDate, atdDate,
      } = this.report;
      const {
        startTime, endTime, ataTime, atdTime,
      } = this.report;

      if (startDate) {
        this.report.voyageStartDateTimeLt = dateTimeHelpers
          .dateAndTimeLtString(startDate, startTime);
      }

      if (endDate) {
        this.report.voyageEndDateTimeLt = dateTimeHelpers.dateAndTimeLtString(endDate, endTime);
      }

      if (ataDate) {
        this.report.ataDateTimeLt = dateTimeHelpers.dateAndTimeLtString(ataDate, ataTime);
      }

      if (atdDate) {
        this.report.atdDateTimeLt = dateTimeHelpers.dateAndTimeLtString(atdDate, atdTime);
      }
    },
    openDeleteDialog() {
      this.showDeleteDialog = true;
    },
    closeDeleteDialog() {
      this.showDeleteDialog = false;
    },
    ataDateSelected() {
      this.ataDateMenu = false;
      this.ataTimeMenu = true;
    },
    atdDateSelected() {
      this.atdDateMenu = false;
      this.atdTimeMenu = true;
    },
    startDateSelected() {
      this.startDateMenu = false;
      this.startTimeMenu = true;
    },
    endDateSelected() {
      this.endDateMenu = false;
      this.endTimeMenu = true;
    },
  },
};
</script>
